import React, { useEffect, useRef, useState } from "react";

import { IDeviceDetails } from "@formant/core/model/IDeviceDetails";
import { ITagSets } from "@formant/core/model/ITagSets";
import { Laptop } from "@formant/ui-kit";

interface IChooseDevicesProps {
    devices?: IDeviceDetails[];
    selectedTags: ITagSets;
    onSelectedDevicesChange?: (selectedDevices: IDeviceDetails[]) => void;
}

export const ChooseDevices: React.FC<IChooseDevicesProps> = ({
    devices = [],
    selectedTags,
    onSelectedDevicesChange,
}) => {
    const [selectedDevices, setSelectedDevices] = useState<Set<string>>(
        new Set(),
    );
    const previousSelectedTagsRef = useRef<ITagSets>({});
    const isInitialRender = useRef(true);
    // Calculate selected devices when tags or devices change
    useEffect(() => {
        // Skip the first render to avoid initial callback
        if (isInitialRender.current) {
            isInitialRender.current = false;

            // Initialize selected devices on first render with filtering
            if (devices.length > 0) {
                let initialSelectedIds = new Set<string>();
                let initialSelectedDevices: IDeviceDetails[] = [];

                if (Object.keys(selectedTags).length === 0) {
                    // If no tags selected, all devices are selected
                    initialSelectedIds = new Set(
                        devices.map(device => device.id),
                    );
                    initialSelectedDevices = [...devices];
                } else {
                    // Filter devices based on existing selectedTags
                    devices.forEach(device => {
                        const isSelected = Object.entries(selectedTags).every(
                            ([category, value]) => {
                                if (device.tags[category]) {
                                    if (value.includes("*")) {
                                        // '*' means any value, just check existence of the tag category
                                        return true;
                                    }
                                    const deviceTagValues = Array.isArray(
                                        device.tags[category],
                                    )
                                        ? (device.tags[category] as string[])
                                        : [device.tags[category] as string];
                                    return value.some(v =>
                                        deviceTagValues.includes(v),
                                    );
                                }
                                return false;
                            },
                        );

                        if (isSelected) {
                            initialSelectedIds.add(device.id);
                            initialSelectedDevices.push(device);
                        }
                    });
                }

                setSelectedDevices(initialSelectedIds);
                if (onSelectedDevicesChange) {
                    onSelectedDevicesChange(initialSelectedDevices);
                }
            }
            return;
        }

        // Check if selectedTags has actually changed
        const tagsChanged =
            JSON.stringify(previousSelectedTagsRef.current) !==
            JSON.stringify(selectedTags);
        previousSelectedTagsRef.current = { ...selectedTags };

        // Only recalculate if tags or devices have changed
        if (!tagsChanged && devices.length > 0) {
            return;
        }

        let newSelectedIds: Set<string>;
        let selectedDevicesList: IDeviceDetails[] = [];

        if (Object.keys(selectedTags).length === 0) {
            // If no tags selected, all devices are selected
            newSelectedIds = new Set(devices.map(device => device.id));
            selectedDevicesList = [...devices];
        } else {
            // Filter devices based on selected tags
            newSelectedIds = new Set<string>();

            devices.forEach(device => {
                // Check if device has all the selected tags
                const isSelected = Object.entries(selectedTags).every(
                    ([category, value]) => {
                        // If the device has this tag category
                        if (device.tags[category]) {
                            // Check if the value matches
                            if (value.includes("*")) {
                                // '*' means any value, just check existence of the tag category
                                return true;
                            }
                            const deviceTagValues = Array.isArray(
                                device.tags[category],
                            )
                                ? (device.tags[category] as string[])
                                : [device.tags[category] as string];
                            return value.some(v => deviceTagValues.includes(v));
                        }
                        return false;
                    },
                );

                if (isSelected) {
                    newSelectedIds.add(device.id);
                    selectedDevicesList.push(device);
                }
            });
        }

        // Update local state
        setSelectedDevices(newSelectedIds);
        // Notify parent component about the change - but only if the callback exists
        if (onSelectedDevicesChange && devices.length > 0 && tagsChanged) {
            onSelectedDevicesChange(selectedDevicesList);
        }

        // Intentionally omit onSelectedDevicesChange from dependencies
    }, [devices, selectedTags]);

    return (
        <div className="w-full">
            <h2 className="text-lg font-semibold text-white mb-4">
                SELECTED DEVICES
                <span className="ml-2 text-sm font-normal">
                    ({selectedDevices.size} of {devices.length})
                </span>
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {devices.map(device => {
                    const isSelected = selectedDevices.has(device.id);

                    return (
                        <div
                            key={device.id}
                            className={`${isSelected ? "bg-formant-color-background-active" : "bg-formant-color-background-tertiary"} rounded-md p-4 flex items-center gap-3 border ${isSelected ? "border-formant-color-border-active" : "border-formant-color-border-tertiary"} transition-colors`}
                        >
                            <Laptop
                                className={`h-5 w-5 ${isSelected ? "text-formant-color-text-active" : "text-formant-color-text-muted"}`}
                            />
                            <span
                                className={`${isSelected ? "text-white" : "text-formant-color-text-muted"} truncate`}
                            >
                                {device.name}
                            </span>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};
